import {
  agendaRruleSchema,
  agendaSlotTypeSchema,
  PRESENTIEL_SLOT_TYPE,
  periodsOfTheDay,
} from '@omecare/utils';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { CreateWorkScheduleSlotBulkApiPayload } from 'node_modules/@omecare/utils/src/types/work-schedule-slots/create-work-schedule-slot-bulk';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import http from '@/utils/http.service';
import { workScheduleSlotSequenceSchema } from '../queries/use-get-office-work-schedules-slots';

export const addWorkScheduleSlotItemSchema = z.object({
  id: z.string().uuid(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  periodOfTheDay: z.enum(periodsOfTheDay),
  doctorAttending: z.object({
    id: z.string().uuid(),
    firstname: z.string(),
    lastname: z.string(),
  }),
  rrule: agendaRruleSchema,
  exdate: z.array(z.string()),
  sourceId: z.string().nullable().default(null),
  sequence: workScheduleSlotSequenceSchema,
  occurenceIndex: z.number().optional(),
  slotType: agendaSlotTypeSchema.optional().default(PRESENTIEL_SLOT_TYPE),
  forceWithoutAssistant: z.coerce.boolean().optional().default(false),
});
export type AddWorkScheduleSlotItemResponse = z.infer<
  typeof addWorkScheduleSlotItemSchema
>;

export const addWorkScheduleSlotResponseSchema = z.object({
  data: z.array(addWorkScheduleSlotItemSchema),
});
export type AddOfficeWorkScheduleSlotResponse = z.infer<
  typeof addWorkScheduleSlotResponseSchema
>;

const addOfficeWorkScheduleSlotBulk = (
  payload: CreateWorkScheduleSlotBulkApiPayload,
) => {
  return http.workScheduleSlots
    .createBulk(payload)
    .then<AddOfficeWorkScheduleSlotResponse>(({ transform }) => {
      return transform.handle({
        attributes: {
          startAt: true,
          endAt: true,
          rrule: true,
          exdate: true,
          sourceId: true,
          occurenceIndex: true,
          slotType: true,
          withAssistante: true,
          forceWithoutAssistant: true,
          dayOfWeek: true,
          periodOfTheDay: true,
        },
        relations: {
          doctorAttending: true,
          sequence: true,
        },
        schema: addWorkScheduleSlotResponseSchema,
      });
    });
};

const useAddOfficeWorkScheduleSlotBulkMutation = (): UseMutationResult<
  AddOfficeWorkScheduleSlotResponse,
  Error,
  CreateWorkScheduleSlotBulkApiPayload
> => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: addOfficeWorkScheduleSlotBulk,
    onSuccess() {
      toast.success(t('http.add-office-work-schedule-slot-bulk.success'));
    },
  });
};

export default useAddOfficeWorkScheduleSlotBulkMutation;
